import React, { FC, useCallback, useEffect, useState } from 'react';
import styles from './AddressBookEdit.module.css';
import { SubHeading } from '../../common/PageHeadings/PageHeadings';
import { Divider, IconButton, List, ListItem, ListItemText, Skeleton, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tooltip, Typography, useMediaQuery, useTheme } from '@mui/material';
import Card from '../../common/Card/Card';
import { toast } from 'react-toastify';
import useAuth from '../../../hooks/useAuth';
import { AddressAWS } from '../../../@types/AWS';
import Button from '../../common/Button';
import { DeleteOutlineOutlined } from '@mui/icons-material';
import EditIcon from '@mui/icons-material/Edit';
import { useConfirmationModalContext } from '../../../context/ConfirmModalContext';
import Address from '../../../@types/Address';
import Shape from '../../../@types/Shape';
import * as yup from 'yup';
import Modal from '../../common/Modal/Modal';
import { yupResolver } from '@hookform/resolvers/yup';
import Form from '../../common/Form/Form';
import AddressField from '../../common/Form/FormFields/AddressField/AddressField';
import { FormProvider } from 'react-hook-form';
import LoadingButton from '../../common/LoadingButton/LoadingButton';
import TextField from '../../common/Form/FormFields/TextField/TextField';
import flatten from 'flat';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import DoNotDisturbOnIcon from '@mui/icons-material/DoNotDisturbOn';
import PendingActionsIcon from '@mui/icons-material/PendingActions';
import { isMadJaxAdmin } from '../../../utils/isMadJaxAdmin';
import CheckBox from '../../common/Form/FormFields/CheckBox/CheckBox';
import "yup-phone-lite";
import validateZipCode from '../../../utils/validateZipCode';
import axios from '../../../api/axios';

interface AddressBookEditProps {

  dealerId?: string;

}

const tableHeaders = ['name', 'address', 'authorized address (leads)', '']

const AddressBookEdit: FC<AddressBookEditProps> = ({ dealerId }) => {

  const { user: currentUser } = useAuth();

  const [loading, setLoading] = useState<boolean>(false);

  const [addressToEdit, setAddressToEdit] = useState<AddressAWS | undefined>(undefined);

  const [addresses, setAddresses] = useState<AddressAWS[]>([]);

  const [editAddressModalVisible, setEditAddressModalVisible] = useState<boolean>(false);

  const [addAddressModalVisible, setAddAddressModalVisible] = useState<boolean>(false);

  const theme = useTheme();

  const isTablet = useMediaQuery(theme.breakpoints.down('tablet'));

  const confirmContext = useConfirmationModalContext();

  const getAddresses = useCallback(async () => {

    try {

      setLoading(true);

      const queryParams = { dealerId: dealerId ?? currentUser?.companyAttributes?.dealerId };

      const response = await axios.get(`/dealer/addresses`, { params: queryParams });

      setAddresses(response.data.data);

    } catch (error: any) {

      console.log(error);

      toast.error('Error loading address book');

    } finally {

      setLoading(false);

    }

  }, [currentUser]);

  const deleteAddress = async (addressToDelete: AddressAWS) => {

    const result = await confirmContext.showConfirmation(

      `Delete Address [${addressToDelete.addressName}]`,

      addressToDelete?.approved && (addressToDelete.authorizedVehicleDealerLocation || addressToDelete?.authorizedAccessoriesDealerLocation)

        ? `This address is used to generate leads. You will not be assigned leads based on proximity of prospects to this address if deleted. Would you like to delete address [${addressToDelete.addressName}]?`

        : `Would you like to delete address [${addressToDelete.addressName}]?`

    )

    if (result) {

      try {

        setAddresses((addresses) => {

          const addressIndex = addresses.findIndex((address => address.sk === addressToDelete.sk));

          addresses.splice(addressIndex, 1);

          return [...addresses];

        });

        await axios.delete('/dealer/address', { data: { pk: addressToDelete.pk, sk: addressToDelete.sk } });

        toast.success(`Address [${addressToDelete.addressName}] deleted`);

      } catch (error) {

        console.log(error);

        setAddresses((addresses) => [...addresses, addressToDelete]);

        toast.error('Server Error');

      }

    }

  }

  useEffect(() => { getAddresses() }, [getAddresses]);

  useEffect(() => {

    if (!addAddressModalVisible || !editAddressModalVisible) getAddresses();

  }, [
    addAddressModalVisible,
    editAddressModalVisible,
    getAddresses
  ]);

  const editAddress = (addressToEdit: AddressAWS) => {

    setAddressToEdit(addressToEdit);

    setEditAddressModalVisible(true);

  }

  return (

    <div className={styles.AddressBookEdit}>

      <Stack sx={{ marginBottom: '16px' }}>

        <SubHeading>Address Book</SubHeading>

      </Stack>

      <Card>

        <Stack direction="column">

          {!loading ?

            (addresses.length ?

              (!isTablet

                ? (

                  <Stack direction="column" spacing={2} alignItems='flex-end'>

                    <TableContainer>

                      <Table>

                        <TableHead>

                          <TableRow>

                            {tableHeaders.map((header, index) => (

                              <TableCell key={index}
                                sx={{
                                  borderLeft: 'none',
                                  borderRight: 'none',
                                  borderTop: 'none',
                                  textTransform: 'uppercase',
                                  ...(header === 'authorized address (leads)' && {
                                    textAlign: 'center'
                                  }),
                                }}>

                                {header}

                              </TableCell>

                            ))}

                          </TableRow>

                        </TableHead>

                        <TableBody>

                          {addresses.map((address, index) => (

                            <TableRow key={index}
                              sx={{
                                '.MuiTableCell-root': {
                                  border: 'none',
                                  paddingTop: '12px',
                                  paddingBottom: '12px'
                                },
                              }}>

                              <TableCell>

                                {address.addressName}

                              </TableCell>

                              <TableCell>

                                {address.street} {address.street2} {address.city}, {address.state}, {address.zip}, {address.country}

                              </TableCell>

                              <TableCell sx={{ textAlign: 'center' }}>

                                {address?.authorizedAccessoriesDealerLocation || address?.authorizedVehicleDealerLocation

                                  ? (address?.approved

                                    ? (

                                      <Tooltip title="Approved">

                                        <CheckCircleRoundedIcon sx={{ color: 'green' }} />

                                      </Tooltip>

                                    )

                                    : (

                                      <Tooltip title="Pending Approval">

                                        <PendingActionsIcon sx={{ color: 'orange' }} />

                                      </Tooltip>
                                    )

                                  )

                                  : (

                                    <Tooltip title="Standard Address">

                                      <DoNotDisturbOnIcon sx={{ color: 'red' }} />

                                    </Tooltip>

                                  )

                                }

                              </TableCell>

                              <TableCell>

                                <Stack direction="row" justifyContent="flex-end">

                                  <Tooltip title="Delete Address" disableInteractive>

                                    <IconButton onClick={() => { deleteAddress(address) }}>

                                      <DeleteOutlineOutlined />

                                    </IconButton>

                                  </Tooltip>

                                  <Tooltip title="Edit Address" disableInteractive>

                                    <IconButton onClick={() => { editAddress(address) }}>

                                      <EditIcon />

                                    </IconButton>

                                  </Tooltip>

                                </Stack>

                              </TableCell>

                            </TableRow>

                          ))}

                        </TableBody>

                      </Table>

                    </TableContainer>

                    <Stack
                      sx={{
                        width: 'fit-content',
                        paddingLeft: '8px',
                        paddingBottom: '8px'
                      }}>

                      <Button
                        onClick={() => { setAddAddressModalVisible(true) }}>

                        Add Address

                      </Button>

                    </Stack>

                  </Stack>

                )

                : (

                  <Stack direction="column" spacing={2} divider={<Divider />}>

                    {addresses.map((address, index) => (

                      <List key={index}
                        sx={{
                          '.MuiListItem-root': {
                            paddingLeft: 0,
                            paddingRight: 0,
                            '.MuiListItemText-root:last-of-type': {
                              textAlign: 'right',
                              maxWidth: '50%'
                            }
                          }
                        }}>

                        <ListItem>

                          <ListItemText>Name</ListItemText>

                          <ListItemText>{address.addressName}</ListItemText>

                        </ListItem>

                        <ListItem>

                          <ListItemText>Address</ListItemText>

                          <ListItemText>{address.street} {address.street2} {address.city}, {address.state}, {address.zip}, {address.country}</ListItemText>

                        </ListItem>

                        <ListItem>

                          <ListItemText>Authorized Address (Leads)</ListItemText>

                          <ListItemText>

                            {address?.authorizedAccessoriesDealerLocation || address?.authorizedVehicleDealerLocation

                              ? (address?.approved

                                ? (

                                  <Tooltip title="Approved">

                                    <CheckCircleRoundedIcon sx={{ color: 'green' }} />

                                  </Tooltip>

                                )

                                : (

                                  <Tooltip title="Pending Approval">

                                    <PendingActionsIcon sx={{ color: 'orange' }} />

                                  </Tooltip>
                                )

                              )

                              : (

                                <></>

                              )

                            }

                          </ListItemText>

                        </ListItem>

                        <Stack direction="column" spacing={2}>

                          <Button onClick={() => deleteAddress(address)}>Delete Address</Button>

                          <Button onClick={() => editAddress(address)}>Edit Address</Button>

                        </Stack>

                      </List>

                    ))}

                    <Button onClick={() => { setAddAddressModalVisible(true) }}>

                      Add Address

                    </Button>

                  </Stack>

                )

              )

              : (

                <Stack
                  direction="column"
                  justifyContent="center"
                  alignItems="center"
                  sx={{ minHeight: '150px' }}
                  spacing={2}>

                  <Typography>

                    You have no addresses saved to your Address Book

                  </Typography>

                  <Button onClick={() => { setAddAddressModalVisible(true) }}>Create New Address</Button>

                </Stack>

              )

            )

            : (<AddressesLoading />)
          }

        </Stack>

      </Card>

      <EditAddressModal
        address={addressToEdit}
        closeModal={() => setEditAddressModalVisible(false)}
        setAddresses={setAddresses}
        isVisible={editAddressModalVisible} />

      <CreateAddressModal
        dealerId={dealerId}
        closeModal={() => setAddAddressModalVisible(false)}
        isVisible={addAddressModalVisible} />

    </div>

  );

}

interface CreateAddressModalProps {

  dealerId?: string;

  closeModal: () => void;

  isVisible: boolean;

}

const CreateAddressModal: FC<CreateAddressModalProps> = ({ dealerId, closeModal, isVisible }) => {

  const { user: currentUser } = useAuth();

  const [loading, setLoading] = useState<boolean>(false);

  const onSubmit = async (formData: Address) => {

    try {

      setLoading(true);

      await axios.post(`/dealer/address`, formData);

      toast.success(`Address added!`);

      closeModal();

    } catch (error: any) {

      console.log(error);

      toast.error('Server Error');

    } finally {

      setLoading(false);

    }

  }

  const onError = (errors: any) => {

    console.log(errors);

    const errorsFlattened: any = flatten(errors);

    const requiredErrors = Object.fromEntries(Object.entries(errorsFlattened).filter(([key, value]) => key.includes('.type')));

    if (Object.entries(requiredErrors).length <= 2) {

      for (const key in requiredErrors) {

        let keySplit = key.split('.');

        keySplit.pop();

        let formField = '';

        for (const fieldPart of keySplit) {

          formField += fieldPart.split(/([A-Z][a-z]+)/).map(s => s.charAt(0).toUpperCase() + s.slice(1)).join(' ');

        }

        toast.error(`Please check [${formField.trim()}] field for errors`);

      }

    } else {

      toast.error('Please check form for errors');

    }

  }

  const createAddressSchema = yup.object<Shape<Address>>({

    addressName: yup.string().required('Please enter an address name'),

    street: yup.string().required('Please enter a street address'),

    street2: yup.string().optional(),

    city: yup.string().required('Please enter a city'),

    state: yup.string().required('Please enter a state / province'),

    stateCode: yup.string().required('Please enter a state code'),

    zip: yup.string().required('Please enter a zip or postal code')
    .when('countryCode', (value) => {
      return yup.string().required(`Invalid zip or postal code for country [${value}]`)
        .test('is-valid-zipcode', `Invalid zip or postal code for country [${value}]`, function () {
          const { zip, countryCode } = this.parent;
          return validateZipCode(countryCode, zip)
        })
    }),

    country: yup.string().required('Please enter a country'),

    countryCode: yup.string().required('Please enter a country code'),

    lat: yup.number().required('Please enter address lat'),

    lng: yup.number().required('Please enter address lng'),

    dealerId: yup.string().required('Please enter a dealerId'),

    approved: yup.boolean().optional(),

    authorizedVehicleDealerLocation: yup.boolean().optional(),

    authorizedAccessoriesDealerLocation: yup.boolean().optional(),

    phone: yup.string().phone().optional().when('countryCode', (value) => {
      return yup.string().phone(value, `Please enter validate phone number for country [${value}]`);
    }),

  });

  const defaultValues = {

    dealerId: dealerId ?? currentUser?.companyAttributes?.dealerId

  }

  return (
    <>

      {isVisible &&

        <Modal title={`Add New Address`} closeModal={closeModal}>

          <Form
            defaultValues={defaultValues}
            onSubmit={onSubmit}
            onError={onError}
            resolver={yupResolver(createAddressSchema)}>

            {(methods) => (

              <FormProvider {...methods}>

                <TextField name={'addressName'} label={"Address Name"} />

                <AddressField name={''} label={''} />

                <Stack
                  direction="column"
                  spacing={2}
                  marginTop={'16px'}>

                  <CheckBox
                    name="authorizedVehicleDealerLocation"
                    label={!isMadJaxAdmin(currentUser) ? 'Request Approval Authorized Vehicle Dealer Location' : 'Authorized Vehicle Dealer Location'}
                    isTrueFalse={true}
                    defaultValue={0} />

                  <CheckBox
                    name="authorizedAccessoriesDealerLocation"
                    label={!isMadJaxAdmin(currentUser) ? 'Request Approval Authorized Vehicle Accessories Dealer Location' : 'Authorized Vehicle Accessories Dealer Location'}
                    isTrueFalse={true}
                    defaultValue={0} />


                  {isMadJaxAdmin(currentUser) &&

                    <CheckBox
                      name="approved"
                      label={'Approve Address For Leads Use'}
                      isTrueFalse={true}
                      defaultValue={0} />

                  }

                  <Stack direction="row" spacing={2} justifyContent="flex-end" sx={{ marginTop: '32px' }}>

                    <Button onClick={closeModal}>Cancel</Button>

                    <LoadingButton loading={loading} type="submit">Save Address</LoadingButton>

                  </Stack>

                </Stack>

              </FormProvider>

            )}

          </Form>

        </Modal>

      }

    </>

  )

}

interface EditAddressModalProps {

  address: AddressAWS | undefined;

  closeModal: () => void;

  setAddresses: React.Dispatch<React.SetStateAction<AddressAWS[]>>;

  isVisible: boolean;

}

const EditAddressModal: FC<EditAddressModalProps> = ({ address, closeModal, setAddresses, isVisible }) => {

  const { user } = useAuth();

  const [loading, setLoading] = useState<boolean>(false);

  const onSubmit = async (formData: AddressAWS) => {

    try {

      if (formData.approved === undefined) {

        formData.approved = false;

      }

      setLoading(true);

      await axios.patch('/dealer/address', formData);

      toast.success(`Address [${formData.addressName}] saved!`);

      setAddresses((addresses) => {

        const addressIndex = addresses.findIndex((address) => address.sk === formData.sk);

        addresses[addressIndex] = formData;

        return [...addresses];

      });

      closeModal();

    } catch (error: any) {

      console.log(error);

      toast.error('Server Error');

    } finally {

      setLoading(false);

    }

  }

  const onError = (errors: any) => {

    console.log(errors);

    toast.error('Please check form for errors');

  }

  const editAddressSchema = yup.object<Shape<AddressAWS>>({

    pk: yup.string().required(),

    sk: yup.string().required(),

    addressName: yup.string().required('Please enter an address name'),

    street: yup.string().required('Please enter a street address'),

    street2: yup.string().optional(),

    city: yup.string().required('Please enter a city'),

    state: yup.string().required('Please enter a state / province'),

    stateCode: yup.string().required('Please enter a state code'),

    zip: yup.string().required('Please enter a zip or postal code'),

    country: yup.string().required('Please enter a country'),

    countryCode: yup.string().required('Please enter a country code'),

    lat: yup.number().required('Please enter address lat'),

    lng: yup.number().required('Please enter address lng'),

    approved: yup.boolean().optional(),

    authorizedVehicleDealerLocation: yup.boolean().optional(),

    authorizedAccessoriesDealerLocation: yup.boolean().optional(),

    dealerId: yup.string().required(),

    phone: yup.string().phone().optional().when('countryCode', (value) => {
      return yup.string().phone(value, `Please enter validate phone number for country [${value}]`);
    }),

  });

  const defaultValues = {

    pk: address?.pk,

    sk: address?.sk,

    addressName: address?.addressName,

    street: address?.street,

    street2: address?.street2,

    city: address?.city,

    state: address?.state,

    stateCode: address?.stateCode,

    zip: address?.zip,

    country: address?.country,

    countryCode: address?.countryCode,

    lat: address?.lat,

    lng: address?.lng,

    addressFormatted: address?.addressFormatted,

    approved: address?.approved,

    authorizedVehicleDealerLocation: address?.authorizedVehicleDealerLocation,

    authorizedAccessoriesDealerLocation: address?.authorizedAccessoriesDealerLocation,

    dealerId: address?.dealerId,

    phone: address?.phone

  }

  return (
    <>
      {isVisible &&

        <Modal title={`Edit Address [${address?.addressName}]`} closeModal={closeModal}>

          <Form
            onSubmit={onSubmit}
            onError={onError}
            resolver={yupResolver(editAddressSchema)}
            defaultValues={defaultValues}>

            {(methods) => (

              <FormProvider {...methods}>

                <TextField name={'addressName'} label={"Address Name"} />

                <AddressField name={''} label={''} showPhone={true} />

                <Stack
                  direction="column"
                  spacing={2}
                  marginTop={'16px'}>

                  <CheckBox
                    name="authorizedVehicleDealerLocation"
                    label={!isMadJaxAdmin(user) ? 'Request Approval Authorized Vehicle Dealer Location' : 'Authorized Vehicle Dealer Location'}
                    isTrueFalse={true}
                    defaultValue={0} />

                  <CheckBox
                    name="authorizedAccessoriesDealerLocation"
                    label={!isMadJaxAdmin(user) ? 'Request Approval Authorized Vehicle Accessories Dealer Location' : 'Authorized Vehicle Accessories Dealer Location'}
                    isTrueFalse={true}
                    defaultValue={0} />


                  {isMadJaxAdmin(user) &&

                    <CheckBox
                      name="approved"
                      label={'Approve Address For Leads Use'}
                      isTrueFalse={true}
                      defaultValue={0} />

                  }

                  <Stack direction="row" spacing={2} justifyContent="flex-end" sx={{ marginTop: '32px' }}>

                    <Button onClick={closeModal}>Cancel</Button>

                    <LoadingButton loading={loading} type="submit">Save Address</LoadingButton>

                  </Stack>

                </Stack>

              </FormProvider>

            )}

          </Form>

        </Modal>

      }

    </>

  )

}

const AddressesLoading = () => {

  const theme = useTheme();

  const isTablet = useMediaQuery(theme.breakpoints.down('tablet'));

  const displayCount = 4;

  return (

    (!isTablet
      ? (

        <TableContainer>

          <Table>

            <TableHead>

              <TableRow>

                {tableHeaders.map((header, index) => (

                  <TableCell
                    key={index}
                    sx={{
                      borderLeft: 'none',
                      borderRight: 'none',
                      borderTop: 'none',
                      textTransform: 'uppercase',
                      ...(header === 'authorized address (leads)' && {
                        textAlign: 'center'
                      })
                    }}>

                    {header}

                  </TableCell>

                ))}

              </TableRow>

            </TableHead>

            <TableBody>

              {Array(displayCount).fill(null).map((value, index) => (

                <TableRow
                  key={index}
                  sx={{
                    '.MuiTableCell-root': {
                      border: 'none',
                      paddingTop: '12px',
                      paddingBottom: '12px'
                    }
                  }}>

                  <TableCell>

                    <Skeleton animation="wave" width={'100%'} height={20} />

                  </TableCell>

                  <TableCell>

                    <Skeleton animation="wave" width={'100%'} height={20} />

                  </TableCell>

                  <TableCell sx={{ textAlign: 'center' }}>

                    <Skeleton animation="wave" variant="circular" width={20} height={20} sx={{ marginLeft: 'auto', marginRight: 'auto' }} />

                  </TableCell>

                  <TableCell>

                    <Stack direction="row" justifyContent="flex-end">

                      <Skeleton animation="wave" width={24} height={24} sx={{ marginRight: '12px' }} />

                      <Skeleton animation="wave" width={24} height={24} />

                    </Stack>

                  </TableCell>

                </TableRow>

              ))}

            </TableBody>

          </Table>

        </TableContainer>

      )

      : (

        <Stack direction="column" spacing={2} divider={<Divider />}>

          {Array(displayCount).fill(null).map((value, index) => (

            <List key={index}
              sx={{
                '.MuiListItem-root': {
                  paddingLeft: 0,
                  paddingRight: 0,
                }
              }}>

              <ListItem>

                <ListItemText>Name</ListItemText>

                <Skeleton animation="wave" width={'50%'} height={20} />

              </ListItem>

              <ListItem>

                <ListItemText>Address</ListItemText>

                <Skeleton animation="wave" width={'50%'} height={20} />

              </ListItem>

              <ListItem>

                <ListItemText>Authorized Address (Leads)</ListItemText>

                <Skeleton animation="wave" width={'50%'} height={20} />

              </ListItem>

            </List>

          ))}

        </Stack>

      )

    )

  )

}

export default AddressBookEdit;
